import React, { useEffect } from 'react'
import { pxToRem } from 'theme'
import {
  AddressInput as AddressInputType,
  EventFrequency,
  PaymentMethod,
  TestType,
  useEventForm,
  EventFieldName,
  getErrorMessage,
  useAddEvent,
  getFormattedDateAndTime,
  getFormattedStartAndEndTimeInMinutes,
  EventFormValues,
  useEvent,
  useDeleteEventDay,
  useEditEvent,
  TestValuesInput,
  Event_event_EventDays,
  WeekDays,
  useEventBySlug,
  useAuth,
  UserPermissions,
  UserRole,
  Lab,
  AddressFieldName,
} from '@modmd/data'
import { ConfirmationDialog, Dialog } from 'components/Dialog'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import TextInput from 'components/TextInput'
import Button from 'components/Button'
import { AddressInput } from 'components/AddressInput'
import { SelectInput } from 'components/SelectInput'
import Datalist from 'components/Datalist'
import { Switch } from 'components/Switch'
import { Text } from 'components/Typography'
import { SelectDaysOfWeek } from 'sharedComponents/SelectDaysOfWeek'
import { useToaster } from 'utils/useToaster'
import { addMinutes, areIntervalsOverlapping, hoursToMinutes, isBefore, isFuture } from 'date-fns'
import { FormikHelpers } from 'formik'
import { isNull } from 'lodash'
import { maskSlug, slugReservedWords } from 'utils/helpers'
import Icon from 'components/Icon'
import { CompaniesDropdown } from 'sharedComponents/CompaniesDropdown'
import { GroupsDropdown } from 'sharedComponents/GroupsDropdown'
import { Sublocation } from 'sharedComponents/Sublocation/Sublocation'
import { AddEventDayDialog } from './AddEventDayDialog'
import { DefaultValuesDialog } from './DefaultValuesDialog'
import { LAB_OPTIONS } from '../TestSessions/utils'

interface Props {
  isOpen?: boolean
  onDismiss: () => void
  refetchEvents: () => void
  eventIdEdit?: string
}

interface NewDay {
  startTime: string
  timeRange: number
  amountOfTests: number
  id?: string
}

const SKELETON_COLUMNS = 4
const USERS_PAGE_LENGTH = 10

export const CreateEventDialog: React.VFC<Props> = ({
  isOpen,
  onDismiss,
  refetchEvents,
  eventIdEdit,
}) => {
  const { hasPermission, hasRoles } = useAuth()
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = React.useState(false)
  const { setToastMessage } = useToaster()
  const [isAddEventDayOpen, setIsAddEventDayOpen] = React.useState(false)
  const [isDefaultValuesOpen, setIsDefaultValuesOpen] = React.useState(false)
  const [isAssociated, setIsAssociated] = React.useState(false)
  const [dateEditValues, setDateEditValues] = React.useState<NewDay>()
  const [addEvent] = useAddEvent()
  const [editEvent] = useEditEvent()
  const { getEventBySlug, eventBySlug } = useEventBySlug()
  const isSuperAdmin = hasRoles([UserRole.SUPER_ADMIN])

  const { data: eventData } = useEvent({
    id: eventIdEdit || '',
    countAll: true,
  })
  const hasEventStarted =
    new Date(
      eventData?.event?.EventDays?.map((day: Event_event_EventDays) => day.startTime as string)
        .sort()
        ?.shift() ?? ''
    ) < new Date()

  const [deleteEventDay] = useDeleteEventDay({
    onCompleted: () => {
      setToastMessage('Event day deleted', 'success')
    },
  })

  const [eventDays, setEventDays] = React.useState<Array<NewDay>>([])

  const finishEventAction = (formikHelpers: FormikHelpers<EventFormValues>) => {
    formikHelpers.resetForm()
    onDismiss()
    refetchEvents()
    setEventDays([])
  }

  const withoutAddress = (address: AddressInputType) =>
    address
      ? Object.entries(address)
          .filter(([label]) => label !== AddressFieldName.COMPLEMENT)
          .some(([, field]) => !field)
      : true

  const eventForm = useEventForm({
    initialValues: {
      [EventFieldName.LAB]: Lab.DendiLax,
    },
    validationSchema: 'onceEvent',
    onSubmit: async (values: EventFormValues, formikHelpers: FormikHelpers<EventFormValues>) => {
      const Address = {
        city: values.Address?.city,
        street: values.Address?.street,
        state: values.Address?.state,
        zip: values.Address?.zip,
        country: values.Address?.country,
        county: values.Address?.county,
        complement: values.Address?.complement,
      } as AddressInputType

      if (!eventIdEdit) {
        const { TestValues, groupId, companyId, ...rest } = values

        await addEvent({
          variables: {
            inputData: {
              ...rest,
              lab: values.lab as Lab,
              testValues: TestValues,
              name: values.name as string,
              Address: withoutAddress(Address) ? null : Address,
              isActive: true,
              daysArray: (values.daysArray as Array<WeekDays>) ?? null,
              eventDays,
              ...(isAssociated && { companyId, groupId }),
            },
          },
        })
        finishEventAction(formikHelpers)
        setToastMessage('Event Created', 'success')
      } else {
        await editEvent({
          variables: {
            inputData: {
              id: eventIdEdit,
              lab: values.lab as Lab,
              name: values.name,
              description: values.description,
              daysArray: values.daysArray as Array<WeekDays>,
              testType: values.testType,
              isActive: values.isActive,
              testValues: values.TestValues,
              eventDays,
              price: values.price,
              isAddressNotificated: values.isAddressNotificated,
              isDescriptionNotificated: values.isDescriptionNotificated,
              Address: withoutAddress(Address) ? null : Address,
              ...(isAssociated
                ? { companyId: values.companyId, groupId: values.groupId }
                : { companyId: null, groupId: null }),
              paymentMethod: values.paymentMethod,
              sublocation: values.sublocation,
            },
          },
        })
        finishEventAction(formikHelpers)
        setToastMessage('Event Edited', 'success')
      }
    },
  })

  React.useEffect(() => {
    if (eventData && eventData.event) {
      void eventForm
        .setValues(({
          ...eventData.event,
          TestValues: {
            rapidCartridgeExpirationDate: eventData.event.TestValues?.rapidCartridgeExpirationDate,
            rapidDockId: eventData.event.TestValues?.rapidDockId,
            rapidExpirationDate: eventData.event.TestValues?.rapidExpirationDate,
            rapidLotNumber: eventData.event.TestValues?.rapidLotNumber,
            rapidSpecimenNumber: eventData.event.TestValues?.rapidSpecimenNumber,
            specimenType: eventData.event.TestValues?.specimenType,
          },
        } as unknown) as EventFormValues)
        .then(async () => {
          await eventForm.validateField('name')
          await eventForm.validateField('description')
          await eventForm.validateField('Address')
        })
      if (eventData.event && eventData.event.EventDays) {
        const days = eventData.event.EventDays.map((day: Event_event_EventDays) => ({
          startTime: day.startTime,
          timeRange: day.timeRange,
          amountOfTests: day.amountOfTests,
          id: day.id,
        }))
        setEventDays(days as NewDay[])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData])

  React.useEffect(() => {
    if (isOpen && eventData?.event?.companyId && eventData?.event?.groupId) {
      setIsAssociated(true)
    } else {
      setIsAssociated(false)
    }

    return () => setIsAssociated(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdEdit, isOpen, eventData])

  const handleAddressChange = React.useCallback(
    async (addressInput: Partial<AddressInputType>) => {
      await eventForm.setFieldValue('Address', { ...addressInput })
      await eventForm.validateField('Address')
    },
    [eventForm]
  )

  const handleAddEventDay = (newDay: NewDay) => {
    const hasOverLap = eventDays.some((days) =>
      areIntervalsOverlapping(
        {
          start: new Date(days.startTime),
          end: addMinutes(new Date(days.startTime), hoursToMinutes(days.timeRange)),
        },
        {
          start: new Date(newDay.startTime),
          end: addMinutes(new Date(newDay.startTime), hoursToMinutes(newDay.timeRange)),
        }
      )
    )
    if (hasOverLap) {
      setToastMessage(
        'We have two event days with conflicting date, check the date entered',
        'danger'
      )
      return false
    }
    const newDays = [...eventDays, newDay]
    setEventDays(newDays)
    setIsAddEventDayOpen(false)
    return true
  }

  const handleEventDefaultValues = (values: TestValuesInput) => {
    void eventForm.setFieldValue('TestValues', values)
  }

  const handleBlurSlug = () => {
    getEventBySlug({ variables: { slug: eventForm.values.slug as string } })
  }

  const handleDeleteEventDay = (id: string | undefined, startTime: string) => {
    if (!isFuture(new Date(startTime))) {
      return
    }

    if (id) {
      void deleteEventDay({
        variables: {
          id,
        },
      })

      setEventDays(eventDays.filter((day: NewDay) => day.startTime !== startTime))
      setIsOpenConfirmationModal(false)
    }

    setEventDays(eventDays.filter((day: NewDay) => day.startTime !== startTime))
    setIsOpenConfirmationModal(false)
  }

  useEffect(() => {
    if (slugReservedWords.includes(eventForm.values.slug as string)) {
      eventForm.setFieldError('slug', 'This slug is not allowed')
      void eventForm.setFieldTouched('slug', true)
    } else if (eventBySlug?.data?.eventBySlug) {
      eventForm.setFieldError('slug', 'This url slug is already in use')
      void eventForm.setFieldTouched('slug', true)
    } else eventForm.setFieldError('slug', undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBySlug.data])

  const EventDaysOnce = ({ isDisabled }: { isDisabled: boolean }) => (
    <>
      <Card.Title
        rightContent={
          <Box>
            <Button
              disabled={
                (hasEventStarted && eventData?.event?.isActive && !!eventIdEdit) || isDisabled
              }
              onClick={() => {
                setIsAddEventDayOpen(true)
              }}
              appearance="ghost"
            >
              {eventForm.values.frequency === EventFrequency.Weekly &&
                eventDays.length === 0 &&
                'Add Event Start Day'}
              {eventForm.values.frequency === EventFrequency.Once && 'Add Event Day'}
            </Button>
          </Box>
        }
      >
        {eventForm.values.frequency === EventFrequency.Once ? 'Event days' : 'Event Start day'}
      </Card.Title>
      <Datalist
        skeletonProps={{
          columns: SKELETON_COLUMNS,
          rows: USERS_PAGE_LENGTH,
        }}
        data={
          eventDays?.map((item: Partial<Event_event_EventDays>) => ({
            ...item,
            rawStartTime: item?.startTime,
            rawTimeRange: item?.timeRange,
            startTime: getFormattedDateAndTime(item?.startTime),
            timeRange: getFormattedStartAndEndTimeInMinutes(
              new Date(item?.startTime),
              hoursToMinutes(item.timeRange!)
            ),
          })) || []
        }
        shouldRenderTableHeader
        showActionIndicators
        renderTableHeader={() => (
          <Datalist.Row gridTemplateColumns="repeat(4, 1fr)">
            <Datalist.HeaderCell label="Date" />
            <Datalist.HeaderCell label="Time Range" />
            <Datalist.HeaderCell label="Amount of Tests" />
            <Datalist.HeaderCell label="Actions" />
          </Datalist.Row>
        )}
        renderTableRow={({
          startTime,
          timeRange,
          amountOfTests,
          rawStartTime,
          rawTimeRange,
          id,
        }) => (
          <Datalist.Row
            gridTemplateColumns="repeat(4, 1fr)"
            onClick={() => {
              if ((!hasEventStarted || !eventData?.event?.isActive) && eventIdEdit && !isDisabled) {
                setIsAddEventDayOpen(true)
                setDateEditValues({
                  startTime: rawStartTime,
                  timeRange: rawTimeRange!,
                  amountOfTests: amountOfTests!,
                  id,
                })
              }
            }}
          >
            <Datalist.Cell label="Date">{startTime}</Datalist.Cell>
            <Datalist.Cell label="Time Range">{timeRange}</Datalist.Cell>
            <Datalist.Cell label="Amount of Tests">{amountOfTests}</Datalist.Cell>
            <Datalist.Cell label="Actions">
              <Button
                disabled={!isFuture(new Date(startTime)) || isDisabled}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setIsOpenConfirmationModal(true)
                }}
                appearance="ghost"
                colorVariant="danger"
                size="small"
              >
                <Icon.Trash />
              </Button>
            </Datalist.Cell>
            <ConfirmationDialog
              title="Schedule session"
              isOpen={isOpenConfirmationModal}
              onDismiss={() => setIsOpenConfirmationModal(false)}
              actions={
                <>
                  <Button
                    onClick={() => setIsOpenConfirmationModal(false)}
                    colorVariant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => handleDeleteEventDay(id, rawStartTime)}>Continue</Button>
                </>
              }
            >
              <Text>Are you sure you want to delete this event day?</Text>
            </ConfirmationDialog>
          </Datalist.Row>
        )}
      />
    </>
  )

  const handleChangeSelectedDays = (data: string) => {
    if (eventForm.values.daysArray && eventForm.values.daysArray.includes(data)) {
      const newSelection = eventForm.values.daysArray.filter((day) => day !== data)
      void eventForm.setFieldValue('daysArray', newSelection)
    } else {
      const newSelection = eventForm.values.daysArray
        ? [...eventForm.values.daysArray, data]
        : [data]
      void eventForm.setFieldValue('daysArray', newSelection)
    }
  }

  const EventDaysWeekly = () => (
    <>
      <SelectDaysOfWeek
        errorMessage={
          eventForm.values.daysArray && eventForm.errors.daysArray
            ? eventForm.errors.daysArray
            : undefined
        }
        onChange={handleChangeSelectedDays}
        daysArray={eventForm.values.daysArray!}
        disabled={!!eventIdEdit}
      />
    </>
  )

  const handleDismiss = () => {
    setEventDays([])
    eventForm.resetForm()
    onDismiss()
  }

  const checkAssociatedValues = () => {
    let error = false
    if (isAssociated) {
      if (!eventForm.values.companyId || isNull(eventForm.values.companyId)) {
        error = true
      }

      if (!eventForm.values.groupId || isNull(eventForm.values.groupId)) {
        error = true
      }
    }

    return error
  }

  const editIsDisabled = (): boolean => {
    if (!hasPermission([UserPermissions.EDIT_CLOSED_EVENTS])) {
      if (eventData && eventData.event && eventData.event.EventDays) {
        const { EventDays } = eventData.event
        const Days: Event_event_EventDays[] = EventDays
        const firstDay = Days && Days[0] ? new Date(Days[0].startTime) : null
        if (eventIdEdit && firstDay) {
          return !isSuperAdmin || !isBefore(new Date(), firstDay)
        }
      } else {
        return false
      }
    }

    return false
  }

  const onlyEditCompany = !!eventData?.event?.finishedAt && !!eventIdEdit

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onDismiss={() => {
          eventForm.resetForm()
          setEventDays([])
          onDismiss()
        }}
        maxWidth={pxToRem(800)}
      >
        <Card>
          <Card.Title onDismiss={handleDismiss}>{eventIdEdit ? 'Edit' : 'Create'} Event</Card.Title>
          <Card.Content display="grid" gridGap="1rem">
            <Box>
              <TextInput
                name={EventFieldName.NAME}
                value={eventForm.values.name || ''}
                label="Event Name"
                onChange={eventForm.handleChange}
                onBlur={async () => {
                  await eventForm.setFieldTouched(EventFieldName.NAME)
                  await eventForm.validateField(EventFieldName.NAME)
                }}
                errorMessage={getErrorMessage(eventForm.touched.name, eventForm.errors.name)}
                disabled={onlyEditCompany}
                isFullWidth
              />
              <TextInput
                name={EventFieldName.DESCRIPTION}
                value={eventForm.values.description || ''}
                label="Description"
                onChange={eventForm.handleChange}
                onBlur={async () => {
                  await eventForm.setFieldTouched(EventFieldName.DESCRIPTION)
                  await eventForm.validateField(EventFieldName.DESCRIPTION)
                }}
                errorMessage={getErrorMessage(
                  eventForm.touched.description,
                  eventForm.errors.description
                )}
                disabled={onlyEditCompany}
                isFullWidth
              />
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Switch
                  size="small"
                  isChecked={!!eventForm.values.isDescriptionNotificated}
                  onChange={() =>
                    eventForm.setFieldValue(
                      'isDescriptionNotificated',
                      !eventForm.values.isDescriptionNotificated
                    )
                  }
                  isDisabled={onlyEditCompany}
                />
                <Text ml=".5rem" fontSize="m">
                  Notify
                </Text>
              </Box>
              <TextInput
                disabled={!!eventIdEdit}
                name={EventFieldName.SLUG}
                value={maskSlug(eventForm.values.slug as string) || ''}
                label="Url slug"
                placeholder="new-event-name"
                maxLength={15}
                onChange={eventForm.handleChange}
                onBlur={handleBlurSlug}
                errorMessage={getErrorMessage(eventForm.touched.slug, eventForm.errors.slug)}
                isFullWidth
              />

              {eventForm.values.paymentMethod === PaymentMethod.CreditCard && (
                <TextInput
                  name={EventFieldName.PRICE}
                  value={eventForm.values.price || ''}
                  type="number"
                  disabled={!hasPermission([UserPermissions.SET_EVENT_PRICE]) || onlyEditCompany}
                  label="Unitary price"
                  placeholder="$ 00.00"
                  maxLength={15}
                  onChange={eventForm.handleChange}
                  onBlur={async () => {
                    await eventForm.setFieldTouched(EventFieldName.PRICE)
                    await eventForm.validateField(EventFieldName.PRICE)
                  }}
                  errorMessage={
                    eventForm.errors.price &&
                    eventForm.errors.price.startsWith('price must be a `number`')
                      ? undefined
                      : eventForm.errors.price
                  }
                  isFullWidth
                />
              )}
              <AddressInput
                isDisabled={(hasEventStarted && eventData?.event?.isActive) || onlyEditCompany}
                onTouched={(touched) =>
                  eventForm.setTouched({ ...eventForm.touched, Address: touched })
                }
                errors={eventForm.errors.Address}
                touched={eventForm.touched.Address}
                value={eventForm.values.Address}
                onChange={handleAddressChange}
              />
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Switch
                  size="small"
                  isDisabled={
                    withoutAddress(eventForm.values.Address as AddressInputType) || onlyEditCompany
                  }
                  isChecked={!!eventForm.values.isAddressNotificated}
                  onChange={() =>
                    eventForm.setFieldValue(
                      'isAddressNotificated',
                      !eventForm.values.isAddressNotificated
                    )
                  }
                />
                <Text ml=".5rem" fontSize="m">
                  Notify
                </Text>
              </Box>
              <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-around">
                <Box flex="2">
                  <SelectInput
                    disabled={!!eventData?.event?.countSamples || onlyEditCompany}
                    name={EventFieldName.TEST_TYPE}
                    label="Test type"
                    value={eventForm.values.testType}
                    errorMessage={(eventForm.touched.testType && eventForm.errors.testType) || ''}
                    onBlur={async () => {
                      await eventForm.setFieldTouched(EventFieldName.TEST_TYPE)
                      await eventForm.validateField(EventFieldName.TEST_TYPE)
                    }}
                    onChange={eventForm.handleChange}
                    options={[
                      { value: TestType.Covid19, label: 'RT-PCR' },
                      { value: TestType.RapidCovid19, label: 'Rapid' },
                      { value: TestType.Antigen, label: 'Sofia Antigen' },
                      { value: TestType.SiennaAntigen, label: 'Sienna Antigen' },
                      { value: TestType.IndicaidAntigen, label: 'Indicaid Antigen' },
                      { value: TestType.SofiaStrep, label: 'Sofia Strep A' },
                      { value: TestType.SofiaInfluenza, label: 'Influenza Antigen' },
                      { value: TestType.Lucira, label: 'Lucira' },
                      { value: TestType.AcceavaStrep, label: 'Acceava Strep A' },
                      { value: TestType.SofiaRsv, label: 'Sofia RSV' },
                      { value: TestType.BioSignFlu, label: 'BioSign® Flu A+B Test' },
                    ]}
                  />
                </Box>
                {hasPermission([UserPermissions.ADD_EVENT_DEFAULT_VALUES]) && (
                  <Box pl="4rem" flex="1">
                    <Button
                      appearance="link"
                      onClick={() => {
                        setIsDefaultValuesOpen(true)
                      }}
                      colorVariant="primary"
                      disabled={onlyEditCompany}
                    >
                      Set default values
                    </Button>
                  </Box>
                )}
              </Box>
              <SelectInput
                value={eventForm.values.lab as string}
                name={EventFieldName.LAB}
                label="Lab"
                options={[{ label: 'Select Lab', value: '' }, ...LAB_OPTIONS.slice(1, 2)]}
                errorMessage={
                  (eventForm.touched.lab && eventForm.errors.lab && 'This field is required.') || ''
                }
                onChange={eventForm.handleChange}
                onBlur={async () => {
                  await eventForm.setFieldTouched(EventFieldName.LAB)
                  await eventForm.validateField(EventFieldName.LAB)
                }}
              />
              <SelectInput
                disabled={!!eventIdEdit}
                name={EventFieldName.FREQUENCY}
                label="Repeat"
                value={eventForm.values.frequency as string}
                errorMessage={(eventForm.touched.frequency && eventForm.errors.frequency) || ''}
                onBlur={async () => {
                  await eventForm.setFieldTouched(EventFieldName.FREQUENCY)
                  await eventForm.validateField(EventFieldName.FREQUENCY)
                }}
                onChange={(e) => {
                  setEventDays([])
                  eventForm.handleChange(e)
                }}
                options={[
                  { value: EventFrequency.Once, label: 'Once' },
                  { value: EventFrequency.Weekly, label: 'Weekly' },
                ]}
              />
              <Sublocation
                sublocation={eventForm?.values?.sublocation || ''}
                refetch={() => {}}
                onChange={eventForm.handleChange}
                isDisabled={onlyEditCompany}
              />
            </Box>
            <Box display="flex" alignItems="center">
              <Switch
                size="small"
                isChecked={isAssociated}
                isDisabled={editIsDisabled()}
                onChange={(value) => setIsAssociated(value)}
              />
              <Text ml=".5rem" fontSize="m">
                Associate to a Company/Group
              </Text>
            </Box>
            {isAssociated && (
              <Box>
                <CompaniesDropdown
                  onChange={(e) => {
                    void eventForm.setFieldValue('companyId', e || null)
                    void eventForm.setFieldValue('groupId', null)
                  }}
                  value={eventForm.values.companyId!}
                  isDisabled={editIsDisabled()}
                />
                <GroupsDropdown
                  onChange={(e) => {
                    void eventForm.setFieldValue('groupId', e)
                  }}
                  value={eventForm.values.groupId || ''}
                  companyId={eventForm.values.companyId!}
                  isDisabled={!eventForm.values.companyId || editIsDisabled()}
                  error={eventForm?.touched?.groupId ? eventForm?.errors?.groupId : ''}
                />
              </Box>
            )}
            {eventForm.values.frequency === EventFrequency.Once ? (
              <EventDaysOnce isDisabled={onlyEditCompany} />
            ) : (
              <>
                <EventDaysWeekly />
                <EventDaysOnce isDisabled={onlyEditCompany} />
              </>
            )}

            <Box display="flex" alignItems="center" justifyContent="space-around">
              {Object.values(PaymentMethod).map((payment) => (
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Switch
                    size="small"
                    isChecked={eventForm.values.paymentMethod === payment}
                    isDisabled={
                      (!!eventIdEdit &&
                        (!hasPermission([UserPermissions.EDIT_EVENT_PAYMENT_METHOD]) ||
                          !!eventData?.event?.countAttendee) &&
                        payment !== eventForm.values.paymentMethod) ||
                      onlyEditCompany
                    }
                    onChange={() => eventForm.setFieldValue('paymentMethod', payment)}
                  />
                  <Text ml=".5rem" fontSize="m">
                    {payment}
                  </Text>
                </Box>
              ))}
            </Box>
            <Card.Actions>
              {eventIdEdit && (
                <Box display="flex" alignItems="center" justifyContent="space-between" mr="2rem">
                  <Switch
                    size="small"
                    isChecked={eventForm.values.isActive || false}
                    onChange={() => eventForm.setFieldValue('isActive', !eventForm.values.isActive)}
                    isDisabled={onlyEditCompany}
                  />
                  <Text ml=".5rem" fontSize="m">
                    Active
                  </Text>
                </Box>
              )}
              <Button colorVariant="secondary" mr="0.25rem" onClick={handleDismiss}>
                Cancel
              </Button>
              <Button
                disabled={
                  !!eventIdEdit && hasPermission([UserPermissions.EDIT_CLOSED_EVENTS])
                    ? false
                    : !eventForm.isValid ||
                      (eventForm.values.frequency === EventFrequency.Once &&
                        eventDays.length === 0) ||
                      (eventForm.values.frequency === EventFrequency.Weekly &&
                        (!eventForm.values.daysArray ||
                          eventForm.values.daysArray.length === 0 ||
                          eventDays.length === 0)) ||
                      checkAssociatedValues()
                }
                onClick={() => {
                  eventForm.handleSubmit()
                }}
                isFetching={eventForm.isSubmitting}
              >
                Save
              </Button>
            </Card.Actions>
          </Card.Content>
        </Card>
      </Dialog>
      <AddEventDayDialog
        isOpen={isAddEventDayOpen}
        onDismiss={() => {
          setIsAddEventDayOpen(false)
          setDateEditValues(undefined)
        }}
        onFinish={handleAddEventDay}
        dateEditValues={dateEditValues}
        onRefetch={refetchEvents}
      />
      <DefaultValuesDialog
        testType={eventForm.values.testType}
        isOpen={isDefaultValuesOpen}
        onDismiss={() => setIsDefaultValuesOpen(false)}
        onFinish={handleEventDefaultValues}
        testValues={eventForm.values.TestValues}
      />
    </>
  )
}
